import { Loop } from "./Loop";
import { Time } from "./Time";
import { Signal } from "./Signal";
import { RendererService } from "../render/RendererService";
import { SceneContext } from "../render/SceneContext";
import { PostFXPipeline } from "../render/PostFXPipeline";
import { PhysicsWorld } from "../physics/PhysicsWorld";
import { InputService } from "../input/InputService";
import { AudioEngine } from "../audio/AudioEngine";
import { AssetService } from "../assets/AssetService";
import { QualityManager } from "../perf/QualityManager";
import { AdaptiveResolution } from "../perf/AdaptiveResolution";
import { Visibility } from "../perf/Visibility";
import type { System, AppContext } from "./System";

export interface AppOptions {
  canvas: HTMLCanvasElement;
  fixedFps?: number;
  enableAudio?: boolean;
  enablePostFX?: boolean;
}

export class App implements AppContext {
  readonly app = this;
  readonly canvas: HTMLCanvasElement;
  readonly time: Time;
  readonly loop: Loop;
  readonly quality: QualityManager;
  readonly renderer: RendererService;
  readonly scene: SceneContext;
  readonly postFX: PostFXPipeline;
  readonly physics: PhysicsWorld;
  readonly input: InputService;
  readonly audio: AudioEngine;
  readonly assets: AssetService;
  readonly adaptive: AdaptiveResolution;
  readonly visibility: Visibility;

  readonly onPause = new Signal<void>();
  readonly onResume = new Signal<void>();

  private readonly systems: System[] = [];
  private disposed = false;
  private booted = false;

  constructor(opts: AppOptions) {
    this.canvas = opts.canvas;
    this.time = new Time();
    this.loop = new Loop();
    this.quality = new QualityManager();
    this.quality.detect();

    this.renderer = new RendererService(this.canvas, this.quality.settings);
    this.scene = new SceneContext(this.renderer);
    this.postFX = new PostFXPipeline(this.renderer, this.scene, this.quality.settings);
    this.physics = new PhysicsWorld(this.quality.settings);
    this.input = new InputService(this.canvas);
    this.audio = new AudioEngine(this.quality.settings);
    this.assets = new AssetService();
    this.adaptive = new AdaptiveResolution(this.renderer);

    this.visibility = new Visibility({
      onHide: () => this.pause(),
      onShow: () => this.resume(),
    });

    this.loop.update.on(({ dt }) => this.tickSystems(dt));
    this.loop.render.on(() => this.postFX.render());
  }

  addSystem<T extends System>(system: T): T {
    this.systems.push(system);
    if (this.booted) {
      system.init?.(this);
    }
    return system;
  }

  removeSystem(system: System): void {
    const idx = this.systems.indexOf(system);
    if (idx >= 0) {
      this.systems.splice(idx, 1);
      system.dispose?.();
    }
  }

  start(): void {
    if (this.booted) return;
    this.booted = true;
    for (const s of this.systems) s.init?.(this);
    this.visibility.attach();
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
  }

  pause(): void {
    if (!this.loop.isRunning) return;
    this.loop.pause();
    this.audio.suspend();
    this.onPause.emit();
  }

  resume(): void {
    if (this.loop.isRunning) return;
    this.loop.resume();
    this.audio.resume();
    this.onResume.emit();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.loop.stop();
    this.visibility.detach();
    for (const s of this.systems) s.dispose?.();
    this.systems.length = 0;
    this.input.dispose();
    this.audio.dispose();
    this.assets.disposeAll();
    this.physics.dispose();
    this.postFX.dispose();
    this.scene.dispose();
    this.renderer.dispose();
  }

  private tickSystems(dt: number): void {
    this.time.tick(dt);
    this.adaptive.sample(dt);
    for (const s of this.systems) s.update?.(this.time.dt, this.time.elapsed);
  }
}
